// NPM
import { defineStore } from 'pinia';

// Services
import Api from '@/services/api';

// Types
import type {
  UserDashboardRelationsGetHelperRelationsResponseType,
  UserDashboardRelationsGetHelperRelationDetailsResponseType,
  UserDashboardRelationsPostHelperReportHelpeeResponseType,
} from '@magma-app/magma-types/user-dashboard/relation';

import type {
  ProspectDashboardGetRelationResponseType,
  ProspectDashboardPostRelationReportResponseType,
} from '@magma-app/magma-types/prospect-dashboard/relation';

const pendingOngoingRequests = new Map<string, Promise<void>>();

export const useRelationsStore = defineStore('relations', {
  state: () => {
    return {
      relations: {
        hasMultipleCampaigns: false,
        finished:
          [] as UserDashboardRelationsGetHelperRelationsResponseType['finished'],
        ongoing:
          [] as UserDashboardRelationsGetHelperRelationsResponseType['onGoing'],
        toProcess:
          [] as UserDashboardRelationsGetHelperRelationsResponseType['toProcess'],
      },
      // TODO: split relation helper and helpee
      relation: null as
        | UserDashboardRelationsGetHelperRelationDetailsResponseType
        | ProspectDashboardGetRelationResponseType
        | null,
      relationHelper:
        null as UserDashboardRelationsGetHelperRelationDetailsResponseType | null,
      relationHelpee: null as ProspectDashboardGetRelationResponseType | null,
    };
  },

  actions: {
    async contactAdmin(payload: {
      file: File | undefined;
      message: string;
      relationMomentId: number;
    }) {
      await Api.userDashboard().relations.helper.contactAdmin(payload);
    },

    async getRelation(payload: {
      relationId?: number;
      type: 'helpee' | 'helper';
    }) {
      if (payload.type === 'helpee') {
        const response: { data: ProspectDashboardGetRelationResponseType } =
          await Api.userDashboard().relations.helpee.getRelation({
            ...(payload.relationId && { relationId: payload.relationId }),
          });
        this.relation = response.data;
        this.relationHelpee = response.data;

        return response.data;
      } else if (payload.type === 'helper' && payload.relationId) {
        const response: {
          data: UserDashboardRelationsGetHelperRelationDetailsResponseType;
        } = await Api.userDashboard().relations.helper.getRelation({
          relationId: payload.relationId,
        });
        this.relation = response.data;
        this.relationHelper = response.data;

        return response.data;
      }
    },

    async getRelations() {
      const response: {
        data: UserDashboardRelationsGetHelperRelationsResponseType;
      } = await Api.userDashboard().relations.helper.getRelations();

      this.relations.hasMultipleCampaigns = response.data.hasMultipleCampaigns;
      this.relations.finished = response.data.finished;
      this.relations.ongoing = response.data.onGoing;
      this.relations.toProcess = response.data.toProcess;
    },

    async markRelationMomentAs(payload: {
      comment?: string;
      rating?: number;
      relationMomentId: number;
      file?: File;
      role: 'done' | 'ongoing' | 'read';
      type?: 'helpee' | 'helper';
    }) {
      if (payload.role === 'done') {
        if (payload.type === 'helpee') {
          await Api.userDashboard().relations.helpee.markAsDone({
            ...(payload.comment && { comment: payload.comment }),
            ...(payload.rating && { rating: payload.rating }),
            relationMomentId: payload.relationMomentId,
            ...(payload.file && { file: payload.file }),
          });
        } else if (payload.type === 'helper') {
          await Api.userDashboard().relations.helper.markAsDone({
            ...(payload.comment && { comment: payload.comment }),
            ...(payload.rating && { rating: payload.rating }),
            relationMomentId: payload.relationMomentId,
            ...(payload.file && { file: payload.file }),
          });
        }
      } else if (payload.role === 'ongoing') {
        const requestKey = `${payload.type || 'unknown'}:${payload.relationMomentId}`;
        const pendingRequest = pendingOngoingRequests.get(requestKey);

        if (pendingRequest) {
          await pendingRequest;
          return;
        }

        const requestPromise = (async () => {
          if (payload.type === 'helpee') {
            await Api.userDashboard().relations.helpee.markAsongoing({
              relationMomentId: payload.relationMomentId,
            });
          } else if (payload.type === 'helper') {
            await Api.userDashboard().relations.helper.markAsongoing({
              relationMomentId: payload.relationMomentId,
            });
          }
        })();

        pendingOngoingRequests.set(requestKey, requestPromise);

        try {
          await requestPromise;
        } finally {
          if (pendingOngoingRequests.get(requestKey) === requestPromise) {
            pendingOngoingRequests.delete(requestKey);
          }
        }
      } else if (payload.role === 'read') {
        await Api.userDashboard().relations.helper.markAsRead({
          relationMomentId: payload.relationMomentId,
        });
      } else {
        throw new Error('Invalid type');
      }
    },

    async report(payload: {
      askRematch?: boolean | null;
      contactedAt?: Date | null;
      comment: string;
      reason: 'ghosting' | 'no-interest' | 'other' | string;
      relationId: number;
      reportScreenshot?: File | null;
      type: 'helpee' | 'helper' | string;
    }) {
      if (payload.type === 'helpee') {
        const query: {
          report: {
            askRematch?: boolean | null;
            contactedAt?: Date | null;
            comment: string;
            reason: 'ghosting' | 'no-interest' | 'other' | string;
            relationId: number;
          };
          reportScreenshot?: File | undefined;
        } = {
          report: {
            askRematch: payload.askRematch,
            comment: payload.comment,
            reason: payload.reason,
            relationId: payload.relationId,
          },
        };

        if (payload.reportScreenshot) {
          query.reportScreenshot = payload.reportScreenshot;
        }

        if (payload.contactedAt) {
          query.report.contactedAt = payload.contactedAt;
        }

        const response: {
          data: ProspectDashboardPostRelationReportResponseType;
        } = await Api.userDashboard().relations.helpee.reportHelper(query);

        this.relation = response.data;
        this.relationHelpee = response.data;
      } else if (payload.type === 'helper') {
        const query: {
          report: {
            contactedAt?: Date | null;
            comment: string;
            reason: 'ghosting' | 'no-interest' | 'other' | string;
            relationId: number;
          };
          reportScreenshot?: File | undefined;
        } = {
          report: {
            comment: payload.comment,
            reason: payload.reason,
            relationId: payload.relationId,
          },
        };

        if (payload.reportScreenshot) {
          query.reportScreenshot = payload.reportScreenshot;
        }

        if (payload.contactedAt) {
          query.report.contactedAt = payload.contactedAt;
        }

        const response: {
          data: UserDashboardRelationsPostHelperReportHelpeeResponseType;
        } = await Api.userDashboard().relations.helper.reportHelpee(query);

        this.relations.finished = response.data.finished;
        this.relations.ongoing = response.data.onGoing;
        this.relations.toProcess = response.data.toProcess;
      }
    },

    async referHelpee(payload: {
      firstname: string;
      lastname: string;
      email: string;
      campaignId: number;
      phoneNumber?: string;
    }) {
      await Api.userDashboard().relations.helper.referHelpee(payload);
    },
  },
});
